import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import {
  TrendingUp,
  Anchor,
  Users,
  Mail,
  ClipboardList,
  UserPlus,
  KeyRound,
  Copy,
} from 'lucide-react';
import { usePageTitle } from '../hooks/usePageTitle';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const authHeaders = () => ({
  withCredentials: true,
  headers: { Authorization: `Bearer ${localStorage.getItem('token') || ''}` },
});

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  } catch {
    return String(iso);
  }
};

export default function AdminDashboard() {
  usePageTitle('Admin Dashboard');

  const [stats, setStats] = useState(null);
  const [inquiries, setInquiries] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('submissions');
  const navigate = useNavigate();

  useEffect(() => {
    const bootstrap = async () => {
      try {
        const me = await axios.get(`${API_URL}/api/auth/me`, authHeaders());
        if (me.data?.role !== 'admin') {
          toast.error('Admin access only');
          navigate('/');
          return;
        }
        await fetchAll();
      } catch (err) {
        toast.error('Please sign in to access admin');
        navigate('/');
      } finally {
        setIsLoading(false);
      }
    };
    bootstrap();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchAll = async () => {
    try {
      const [statsRes, inquiriesRes, subsRes, usersRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/stats`, authHeaders()),
        axios.get(`${API_URL}/api/inquiries`, authHeaders()),
        axios.get(`${API_URL}/api/admin/listing-submissions`, authHeaders()),
        axios.get(`${API_URL}/api/admin/users`, authHeaders()),
      ]);
      setStats(statsRes.data);
      setInquiries(inquiriesRes.data);
      setSubmissions(subsRes.data);
      setUsers(usersRes.data);
    } catch (err) {
      console.error('Admin data error', err);
      toast.error('Failed to load admin data');
    }
  };

  const updateInquiryStatus = async (inquiryId, status) => {
    try {
      await axios.put(
        `${API_URL}/api/inquiries/${inquiryId}/status?status=${status}`,
        {},
        authHeaders()
      );
      toast.success('Status updated');
      fetchAll();
    } catch {
      toast.error('Failed to update status');
    }
  };

  const updateSubmissionStatus = async (submissionId, status) => {
    try {
      await axios.put(
        `${API_URL}/api/admin/listing-submissions/${submissionId}/status?status=${status}`,
        {},
        authHeaders()
      );
      toast.success('Submission updated');
      fetchAll();
    } catch {
      toast.error('Failed to update submission');
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div
      className="min-h-screen pt-32 px-6 md:px-12 lg:px-24 max-w-[1600px] mx-auto pb-24"
      data-testid="admin-dashboard"
    >
      <h1
        className="text-5xl md:text-6xl font-medium tracking-tight mb-12 text-white"
        style={{ fontFamily: 'Playfair Display, serif' }}
        data-testid="admin-title"
      >
        Admin <span className="text-gold">Dashboard</span>
      </h1>

      {stats && (
        <div
          className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-12"
          data-testid="admin-stats"
        >
          <StatCard icon={<Anchor size={22} />} label="Yachts" value={stats.total_yachts} />
          <StatCard icon={<Mail size={22} />} label="Inquiries" value={stats.total_inquiries} highlight={stats.new_inquiries} />
          <StatCard icon={<ClipboardList size={22} />} label="Listing Submissions" value={stats.total_listing_submissions || 0} highlight={stats.new_listing_submissions || 0} />
          <StatCard icon={<TrendingUp size={22} />} label="Quiz Leads" value={stats.total_quiz_leads || 0} />
          <StatCard icon={<Users size={22} />} label="Users" value={stats.total_users} />
          <StatCard icon={<UserPlus size={22} />} label="New Inquiries" value={stats.new_inquiries} accent />
        </div>
      )}

      <div className="flex items-center gap-2 mb-8 border-b border-white/10 flex-wrap" data-testid="admin-tabs">
        <TabButton active={activeTab === 'submissions'} onClick={() => setActiveTab('submissions')} testId="tab-submissions">
          Listing Submissions ({submissions.length})
        </TabButton>
        <TabButton active={activeTab === 'users'} onClick={() => setActiveTab('users')} testId="tab-users">
          Registered Users ({users.length})
        </TabButton>
        <TabButton active={activeTab === 'inquiries'} onClick={() => setActiveTab('inquiries')} testId="tab-inquiries">
          Inquiries ({inquiries.length})
        </TabButton>
        <TabButton active={activeTab === 'reset-tools'} onClick={() => setActiveTab('reset-tools')} testId="tab-reset-tools">
          Password Reset Tools
        </TabButton>
      </div>

      {activeTab === 'submissions' && (
        <SubmissionsTable submissions={submissions} onUpdate={updateSubmissionStatus} />
      )}
      {activeTab === 'users' && <UsersTable users={users} />}
      {activeTab === 'inquiries' && (
        <InquiriesTable inquiries={inquiries} onUpdate={updateInquiryStatus} />
      )}
      {activeTab === 'reset-tools' && <ResetLinkGenerator />}
    </div>
  );
}

// ----- Small components ---------------------------------------------------

function StatCard({ icon, label, value, highlight, accent }) {
  return (
    <div className="glass-light p-5 border border-white/5">
      <div className="flex items-center gap-3 mb-3 text-gold">{icon}
        <h3 className="text-[10px] uppercase tracking-[0.2em] text-white/60">{label}</h3>
      </div>
      <p className={`text-3xl font-semibold ${accent ? 'text-gold' : 'text-white'}`}>
        {value ?? 0}
      </p>
      {highlight != null && highlight > 0 && !accent && (
        <p className="text-xs text-gold mt-1">+{highlight} new</p>
      )}
    </div>
  );
}

function TabButton({ active, onClick, children, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`px-4 py-3 text-xs uppercase tracking-[0.2em] border-b-2 transition-colors ${
        active
          ? 'border-gold text-gold'
          : 'border-transparent text-white/60 hover:text-white'
      }`}
    >
      {children}
    </button>
  );
}

function StatusBadge({ status }) {
  const map = {
    new: 'bg-gold/20 text-gold',
    reviewing: 'bg-blue-500/20 text-blue-300',
    approved: 'bg-green-500/20 text-green-400',
    rejected: 'bg-red-500/20 text-red-400',
    contacted: 'bg-blue-500/20 text-blue-300',
    resolved: 'bg-green-500/20 text-green-400',
  };
  return (
    <span
      className={`px-3 py-1 text-[10px] uppercase tracking-[0.2em] rounded-full ${
        map[status] || 'bg-white/10 text-white/70'
      }`}
    >
      {status}
    </span>
  );
}

// ----- Tables -------------------------------------------------------------

function SubmissionsTable({ submissions, onUpdate }) {
  if (submissions.length === 0) {
    return (
      <div className="glass-light p-12 text-center text-white/60" data-testid="submissions-empty">
        No listing submissions yet. They will appear here as users submit via <span className="text-gold">/list/new</span>.
      </div>
    );
  }
  return (
    <div className="glass-light overflow-x-auto" data-testid="submissions-table">
      <table className="w-full min-w-[900px]">
        <thead>
          <tr className="border-b border-white/10">
            <Th>Title</Th>
            <Th>Category</Th>
            <Th>Submitter</Th>
            <Th>Contact</Th>
            <Th>Location</Th>
            <Th>Price</Th>
            <Th>Status</Th>
            <Th>Date</Th>
            <Th>Actions</Th>
          </tr>
        </thead>
        <tbody>
          {submissions.map((s) => (
            <tr
              key={s.submission_id}
              className="border-b border-white/5 hover:bg-white/5 transition-colors"
              data-testid={`submission-row-${s.submission_id}`}
            >
              <Td><span className="text-white font-medium">{s.title}</span></Td>
              <Td>
                <span className="text-xs text-white/80">{s.category_title}</span>
                <span className="block text-[10px] text-white/40">{s.subcategory_name}</span>
              </Td>
              <Td>
                <span className="text-white">{s.submitter_name || '—'}</span>
                <span className="block text-[10px] text-white/40">
                  {s.submitter_user_id ? 'logged-in' : 'guest'}
                </span>
              </Td>
              <Td>
                <a className="text-gold text-xs" href={`mailto:${s.email}`}>{s.email}</a>
                {s.phone && <span className="block text-[10px] text-white/40">{s.phone}</span>}
              </Td>
              <Td><span className="text-white/70 text-xs">{s.location || '—'}</span></Td>
              <Td><span className="text-white/70 text-xs">{s.price || '—'}</span></Td>
              <Td><StatusBadge status={s.status} /></Td>
              <Td><span className="text-white/50 text-xs">{formatDate(s.created_at)}</span></Td>
              <Td>
                <div className="flex flex-wrap gap-1">
                  {s.status !== 'reviewing' && (
                    <Button
                      variant="ghost"
                      className="text-xs text-blue-300 h-7 px-2"
                      onClick={() => onUpdate(s.submission_id, 'reviewing')}
                      data-testid={`submission-review-${s.submission_id}`}
                    >
                      Review
                    </Button>
                  )}
                  {s.status !== 'approved' && (
                    <Button
                      variant="ghost"
                      className="text-xs text-green-400 h-7 px-2"
                      onClick={() => onUpdate(s.submission_id, 'approved')}
                      data-testid={`submission-approve-${s.submission_id}`}
                    >
                      Approve
                    </Button>
                  )}
                  {s.status !== 'rejected' && (
                    <Button
                      variant="ghost"
                      className="text-xs text-red-400 h-7 px-2"
                      onClick={() => onUpdate(s.submission_id, 'rejected')}
                      data-testid={`submission-reject-${s.submission_id}`}
                    >
                      Reject
                    </Button>
                  )}
                </div>
              </Td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function UsersTable({ users }) {
  if (users.length === 0) {
    return (
      <div className="glass-light p-12 text-center text-white/60" data-testid="users-empty">
        No registered users yet.
      </div>
    );
  }
  return (
    <div className="glass-light overflow-x-auto" data-testid="users-table">
      <table className="w-full min-w-[700px]">
        <thead>
          <tr className="border-b border-white/10">
            <Th>Name</Th>
            <Th>Email</Th>
            <Th>Auth</Th>
            <Th>User ID</Th>
            <Th>Joined</Th>
          </tr>
        </thead>
        <tbody>
          {users.map((u) => (
            <tr
              key={u.user_id}
              className="border-b border-white/5 hover:bg-white/5 transition-colors"
              data-testid={`user-row-${u.user_id}`}
            >
              <Td>
                <div className="flex items-center gap-3">
                  {u.picture ? (
                    <img
                      src={u.picture}
                      alt={u.name}
                      className="w-8 h-8 rounded-full border border-white/10"
                    />
                  ) : (
                    <span className="w-8 h-8 rounded-full border border-white/10 bg-white/5 flex items-center justify-center text-gold text-xs">
                      {u.name?.[0]?.toUpperCase() || 'U'}
                    </span>
                  )}
                  <span className="text-white font-medium">{u.name}</span>
                </div>
              </Td>
              <Td>
                <a className="text-gold text-xs" href={`mailto:${u.email}`}>{u.email}</a>
              </Td>
              <Td>
                <span className="text-[10px] uppercase tracking-[0.2em] text-white/60 border border-white/10 px-2 py-1">
                  {u.auth_provider || 'email'}
                </span>
              </Td>
              <Td><span className="text-white/40 text-[11px] font-mono">{u.user_id}</span></Td>
              <Td><span className="text-white/50 text-xs">{formatDate(u.created_at)}</span></Td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function InquiriesTable({ inquiries, onUpdate }) {
  if (inquiries.length === 0) {
    return (
      <div className="glass-light p-12 text-center text-white/60" data-testid="inquiries-empty">
        No inquiries yet.
      </div>
    );
  }
  return (
    <div className="glass-light overflow-x-auto" data-testid="inquiries-table">
      <table className="w-full min-w-[800px]">
        <thead>
          <tr className="border-b border-white/10">
            <Th>Name</Th>
            <Th>Email</Th>
            <Th>Yacht</Th>
            <Th>Message</Th>
            <Th>Status</Th>
            <Th>Actions</Th>
          </tr>
        </thead>
        <tbody>
          {inquiries.map((i) => (
            <tr
              key={i.inquiry_id}
              className="border-b border-white/5"
              data-testid={`inquiry-row-${i.inquiry_id}`}
            >
              <Td><span className="text-white">{i.name}</span></Td>
              <Td><a className="text-gold text-xs" href={`mailto:${i.email}`}>{i.email}</a></Td>
              <Td><span className="text-white/70 text-xs">{i.yacht_name || 'General'}</span></Td>
              <Td>
                <span className="text-white/70 text-xs block max-w-xs truncate">{i.message}</span>
              </Td>
              <Td><StatusBadge status={i.status} /></Td>
              <Td>
                <div className="flex gap-1">
                  {i.status !== 'contacted' && (
                    <Button
                      variant="ghost"
                      className="text-xs text-blue-300 h-7 px-2"
                      onClick={() => onUpdate(i.inquiry_id, 'contacted')}
                      data-testid={`mark-contacted-${i.inquiry_id}`}
                    >
                      Contacted
                    </Button>
                  )}
                  {i.status !== 'resolved' && (
                    <Button
                      variant="ghost"
                      className="text-xs text-green-400 h-7 px-2"
                      onClick={() => onUpdate(i.inquiry_id, 'resolved')}
                      data-testid={`mark-resolved-${i.inquiry_id}`}
                    >
                      Resolved
                    </Button>
                  )}
                </div>
              </Td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Th({ children }) {
  return (
    <th className="text-left p-4 text-[10px] uppercase tracking-[0.2em] text-white/60 font-medium">
      {children}
    </th>
  );
}
function Td({ children }) {
  return <td className="p-4 align-top">{children}</td>;
}


// ----- Password Reset Link Generator (admin-only tool) --------------------

function ResetLinkGenerator() {
  const [email, setEmail] = useState('');
  const [link, setLink] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleGenerate = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setLink(null);
    try {
      const res = await axios.post(
        `${API_URL}/api/admin/generate-reset-link`,
        { email },
        authHeaders()
      );
      setLink(res.data);
      toast.success('Reset link generated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not generate link');
    } finally {
      setIsLoading(false);
    }
  };

  const copyLink = async () => {
    if (!link?.reset_url) return;
    try {
      await navigator.clipboard.writeText(link.reset_url);
      toast.success('Link copied');
    } catch {
      toast.error('Could not copy — please copy manually');
    }
  };

  return (
    <div className="max-w-2xl" data-testid="reset-tools-panel">
      <div className="glass-light border border-white/5 p-8">
        <div className="flex items-center gap-3 mb-2 text-gold">
          <KeyRound size={20} />
          <h2 className="text-[11px] uppercase tracking-[0.3em]">Generate password reset link</h2>
        </div>
        <p className="text-sm text-white/60 leading-relaxed mb-6">
          While the email-sending domain is being verified on Resend, use this tool to generate a
          one-time reset link for any user. Copy the link and share it with them directly. Each link
          is single-use and expires in 60 minutes.
        </p>

        <form onSubmit={handleGenerate} className="space-y-4" data-testid="reset-link-form">
          <div>
            <Label htmlFor="reset-link-email" className="text-white/80">User email</Label>
            <Input
              id="reset-link-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="bg-transparent border-b border-white/20 rounded-none px-0 focus:border-gold"
              placeholder="user@example.com"
              required
              data-testid="reset-link-email-input"
            />
          </div>
          <Button
            type="submit"
            className="bg-gold text-[#0A192F] hover:bg-[#E5C55D] text-xs uppercase tracking-[0.2em]"
            disabled={isLoading}
            data-testid="reset-link-generate-btn"
          >
            {isLoading ? 'Generating...' : 'Generate link'}
          </Button>
        </form>

        {link && (
          <div className="mt-8 pt-6 border-t border-white/10" data-testid="reset-link-result">
            <p className="text-[10px] uppercase tracking-[0.3em] text-gold mb-2">Reset link for {link.email}</p>
            <div className="flex items-stretch gap-2">
              <input
                type="text"
                readOnly
                value={link.reset_url}
                className="flex-1 bg-[#0A192F] border border-white/15 px-3 py-2 text-xs text-white/80 font-mono break-all"
                onFocus={(e) => e.target.select()}
                data-testid="reset-link-output"
              />
              <Button
                type="button"
                onClick={copyLink}
                className="bg-white/5 hover:bg-white/10 text-white border border-white/10 text-xs uppercase tracking-[0.2em] px-4"
                data-testid="reset-link-copy-btn"
              >
                <Copy size={14} className="mr-1.5" /> Copy
              </Button>
            </div>
            <p className="mt-3 text-xs text-white/50">
              Expires in {link.expires_in_minutes} minutes ·{' '}
              {new Date(link.expires_at).toLocaleString(undefined, {
                dateStyle: 'medium',
                timeStyle: 'short',
              })}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
